import type { LngLatLike } from "maplibre-gl";

export type WorldPoint = { x: number; y: number };
export type DisplayPoint = [number, number];

// A fixed, versioned Mercator display window. Canonical database coordinates
// stay in world units. This covers the current bounds with room for growth.
export const DISPLAY_WINDOW = {
  version: 1,
  centerX: 3072,
  centerY: 2048,
  span: 65536,
  zoomOffset: 7,
} as const;

export function worldToDisplay({ x, y }: WorldPoint): DisplayPoint {
  const longitude = ((x - DISPLAY_WINDOW.centerX) / DISPLAY_WINDOW.span) * 360;
  const mercatorY = 0.5 - (y - DISPLAY_WINDOW.centerY) / DISPLAY_WINDOW.span;
  const latitude = (Math.atan(Math.sinh(Math.PI * (1 - 2 * mercatorY))) * 180) / Math.PI;
  return [longitude, latitude];
}

export function displayToWorld(point: { lng: number; lat: number }): WorldPoint {
  const latitude = Math.max(-85.05112878, Math.min(85.05112878, point.lat));
  const radians = (latitude * Math.PI) / 180;
  const mercatorY = (1 - Math.asinh(Math.tan(radians)) / Math.PI) / 2;
  return {
    x: DISPLAY_WINDOW.centerX + (point.lng / 360) * DISPLAY_WINDOW.span,
    y: DISPLAY_WINDOW.centerY + (0.5 - mercatorY) * DISPLAY_WINDOW.span,
  };
}

export function worldBoundsToDisplay(bounds: {
  min_x: number; min_y: number; max_x: number; max_y: number;
}): [[number, number], [number, number]] {
  return [worldToDisplay({ x: bounds.min_x, y: bounds.min_y }), worldToDisplay({ x: bounds.max_x, y: bounds.max_y })];
}

export function legacyToDisplayZoom(zoom: number): number {
  return zoom + DISPLAY_WINDOW.zoomOffset;
}

export function displayToLegacyZoom(zoom: number): number {
  return zoom - DISPLAY_WINDOW.zoomOffset;
}

export function asLngLat(point: WorldPoint): LngLatLike {
  return worldToDisplay(point);
}
